import { action, computed, makeObservable, observable, reaction } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { createContext, useContext, useEffect } from "react";

import {
  DECISIONS_API,
  DEFAULT_DECISION_QUESTIONS,
  DEFAULT_MODEL_NAMES,
  HUGGINGFACE_PROVIDER,
  OPENAI_DEFAULT_MODELS_BY_API,
  OPENROUTER_DEFAULT_MODELS_BY_API,
} from "../constants";
import {
  convertToBlueprintModel,
  getModelParams,
  mapInitialDataWithExistingParams,
  mergeModelParametersOnSwitch,
} from "../utils";

import { ParameterStore } from "./parameter-store";
import { Model, ParameterProviderProps } from "./types";

import type { Provider } from "@/queries/models/types";
import { Model as ApiModel, Parameter } from "@/queries/models/types";
import { DecisionQuestion, JSONSchema } from "@/types/prompt-blueprint";
import { PlaygroundStoreContext } from "@/components/Playground/playground-store";
import { useModelsStore } from "@/providers/ProviderAndModelsProvider/models-store";
import { useLLMProviderStore } from "@/providers/ProviderAndModelsProvider/provider-store";
import { PromptBlueprint } from "@/types";
import { ensureDefaultApiType } from "@/utils/prompt-blueprint";

export class ParametersStore {
  @observable private _parameters = new Map<string, ParameterStore>();
  @observable private _modelName?: string;
  @observable private _modelProvider?: Provider;
  @observable private _inferenceClientName?: string | null;
  @observable private _providerBaseURL?: string | null;
  @observable private _isPromptChat?: boolean;
  @observable private _providerList?: Provider[];
  @observable private _models: ApiModel[] = [];
  @observable private _JSONSchema?: JSONSchema | null = null;
  @observable private _decisionQuestions: DecisionQuestion[] = [];
  @observable private _metadata?: PromptBlueprint["metadata"];
  @observable private onUpdate?: (promptData: Partial<PromptBlueprint>) => void;
  @observable private _providerId?: number | null;
  @observable private _selectedAPI?: string | null;
  @observable private _messages?: string[];

  constructor(
    promptData?: Partial<PromptBlueprint>,
    apiProviders?: Provider[],
    isPromptChat = false,
    onUpdate?: (promptData: Partial<PromptBlueprint>) => void,
  ) {
    makeObservable(this);

    // Ensure default API version for OpenAI providers
    const normalizedPromptData = promptData ? ensureDefaultApiType(promptData) : promptData;

    this._modelName =
      normalizedPromptData?.metadata?.model?.model_config_display_name ||
      normalizedPromptData?.metadata?.model?.name;

    this._modelProvider = apiProviders?.find(
      p =>
        p.id === normalizedPromptData?.provider_id ||
        p.name === normalizedPromptData?.metadata?.model?.provider,
    );
    this._inferenceClientName =
      normalizedPromptData?.inference_client_name ||
      (this._modelProvider?.name === HUGGINGFACE_PROVIDER
        ? normalizedPromptData?.metadata?.model?.model_config_display_name
        : undefined);
    this._metadata = normalizedPromptData?.metadata;
    this._providerBaseURL = normalizedPromptData?.provider_base_url_name;
    this._isPromptChat = isPromptChat;
    this._providerList = apiProviders;
    this._providerId = normalizedPromptData?.provider_id;
    this.onUpdate = onUpdate;
    this._selectedAPI = normalizedPromptData?.metadata?.model?.api_type || null;

    if (normalizedPromptData?.metadata?.model?.parameters?.response_format) {
      this._JSONSchema =
        normalizedPromptData.metadata.model.parameters.response_format?.json_schema || null;
    }

    this._decisionQuestions =
      normalizedPromptData?.metadata?.model?.parameters?.questions ?? DEFAULT_DECISION_QUESTIONS;

    this._parameters = new Map();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setParametersFromArray(parameters: any[]) {
    this._parameters.clear();

    if (!parameters?.length) {
      return;
    }

    parameters.forEach(param => {
      this._parameters.set(param.param_id, new ParameterStore(param));
    });

    this.setJSONSchema(this._JSONSchema || null);
  }

  private getParametersForModel(
    provider?: string,
    model?: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any[] {
    if (!provider) {
      return [];
    }

    if (provider === HUGGINGFACE_PROVIDER) {
      const huggingFaceModel = this._models?.find(
        m => m.display_name === model || m.llm_model_name === model,
      );

      return huggingFaceModel?.params || [];
    }

    return getModelParams(this._models, model || "");
  }

  private findDefaultModelForProvider(provider: string): string | null {
    if (!this._models || provider === HUGGINGFACE_PROVIDER) {
      return null;
    }

    const isOpenAI = provider === "openai" || provider === "openai.azure";

    if (isOpenAI && this._selectedAPI) {
      const apiDefault = OPENAI_DEFAULT_MODELS_BY_API[this._selectedAPI];
      if (apiDefault) {
        const model = this._models.find(m => m.llm_model_name === apiDefault);
        if (model) {
          return model.llm_model_name;
        }
      }
    }

    if (provider === "openrouter" && this._selectedAPI) {
      const apiDefault = OPENROUTER_DEFAULT_MODELS_BY_API[this._selectedAPI];
      if (apiDefault) {
        const model = this._models.find(m => m.llm_model_name === apiDefault);
        if (model) {
          return model.llm_model_name;
        }
      }
    }

    const defaultModelName = DEFAULT_MODEL_NAMES[provider as keyof typeof DEFAULT_MODEL_NAMES];

    if (!defaultModelName) {
      return null;
    }

    const defaultModel = this._models.find(model => model.llm_model_name === defaultModelName);

    return defaultModel ? defaultModel.llm_model_name : null;
  }

  /** Same filtering as ModelDropdown so we do not pick e.g. DALL-E for chat-completions. */
  private modelMatchesSelectedApi(m: ApiModel): boolean {
    const api = this.selectedAPI;
    const providerName = this._modelProvider?.name;
    const isOpenAI = providerName === "openai" || providerName === "openai.azure";
    const isOpenRouter = providerName === "openrouter";
    if (!api || (!isOpenAI && !isOpenRouter)) {
      return true;
    }
    if (isOpenRouter) {
      const apis = m.supported_apis ?? [];
      if (api === "images") {
        return apis.includes("images");
      }
      if (api === "video") {
        return apis.includes("video");
      }
      if (api === "speech") {
        return apis.includes("speech");
      }
      if (api === DECISIONS_API) {
        return apis.includes(DECISIONS_API);
      }
      // Chat: any chat-capable model. Multimodal models (e.g. openai/gpt-5-image)
      // advertise both "chat" and "images", so they must appear under Chat too —
      // not only Images. Models with no api metadata fall back to chat.
      return apis.includes("chat") || apis.length === 0;
    }
    if (m.supported_apis && m.supported_apis.length > 0) {
      return m.supported_apis.includes(api);
    }
    return api !== "images" && api !== DECISIONS_API;
  }

  private resolveDefaultModelRow(): ApiModel | undefined {
    if (!this._modelProvider || !this._models.length) {
      return undefined;
    }

    const defaultModelName = this.findDefaultModelForProvider(this._modelProvider.name);
    if (!defaultModelName) {
      return undefined;
    }

    return this._models.find(
      m =>
        (m.llm_model_name === defaultModelName || m.display_name === defaultModelName) &&
        this.modelMatchesSelectedApi(m),
    );
  }

  @action
  setPromptChatModeAndDefaultModel(isPromptChat: boolean) {
    this._isPromptChat = isPromptChat;
    if (!this._modelProvider) {
      return;
    }

    const defaultModel = this.resolveDefaultModelRow();

    if (defaultModel) {
      this.updateModel({
        name: defaultModel.display_name || defaultModel.llm_model_name,
        provider: this._modelProvider.name,
        parameters: this.getParametersForModel(
          this._modelProvider.name,
          defaultModel.llm_model_name,
        ),
      });
    } else {
      this._modelName = undefined;
      this.setParametersFromArray([]);
      this.updateExternalData();
    }
  }

  @computed get apiModels() {
    return this._providerList;
  }

  @computed get models() {
    return this._models;
  }

  @computed get metadata() {
    return this._metadata;
  }

  @computed get isPromptChat() {
    // Media APIs always use the single-prompt template, even for is_chat models.
    const api = this.selectedAPI;
    if (api === "images" || api === "video" || api === "speech") {
      return false;
    }
    return this._isPromptChat;
  }

  @computed get parameters() {
    if (this.selectedAPI === DECISIONS_API) {
      return [];
    }
    return Array.from(this._parameters.values());
  }

  @computed get parametersMap() {
    return this._parameters;
  }

  @computed get modelName() {
    return this._modelName;
  }

  @computed get provider() {
    return this._modelProvider;
  }

  @computed get inferenceClientName() {
    return this._inferenceClientName;
  }

  @computed get providerBaseURL() {
    return this._providerBaseURL;
  }

  @computed get isCustomParametersSet() {
    return !!this._modelName && !!this._modelProvider && this._parameters.size > 0;
  }

  @computed private get selectedModelRow(): ApiModel | undefined {
    const selectedModelName =
      this._modelProvider?.name === HUGGINGFACE_PROVIDER
        ? this._inferenceClientName
        : this._modelName;
    return this._models?.find(
      m => m.display_name === selectedModelName || m.llm_model_name === selectedModelName,
    );
  }

  @computed get requiresStreaming() {
    return Boolean(this.selectedModelRow?.model_catalog?.requires_streaming);
  }

  @computed get disallowsStreaming() {
    return Boolean(this.selectedModelRow?.model_catalog?.disallows_streaming);
  }

  @computed get promptData(): Partial<PromptBlueprint> | undefined {
    if (!this._modelProvider) {
      return undefined;
    }

    const parameters = Array.from(this._parameters.values()).map(p => p.getParameter);

    const selectedModelName =
      this._modelProvider.name === HUGGINGFACE_PROVIDER
        ? this._inferenceClientName
        : this._modelName;
    const selectedModel = this._models?.find(
      m => m.display_name === selectedModelName || m.llm_model_name === selectedModelName,
    );

    const isTypedCustomModel =
      this._modelProvider.name !== HUGGINGFACE_PROVIDER && this._modelName && !selectedModel;
    const modelName = selectedModel?.llm_model_name || this._modelName;
    const isCustomModel = Boolean(selectedModel?.workspace_id) || isTypedCustomModel;
    const provider = this._providerList?.find(p => p.name === this._modelProvider?.name);
    const isCustomProvider = Boolean(provider?.workspace_id);

    const getModelConfigDisplayName = (): string | null => {
      const shouldShowDisplayName =
        selectedModel &&
        (selectedModel.display_name !== selectedModel.llm_model_name || isCustomModel);

      if (shouldShowDisplayName) {
        return selectedModel.display_name;
      }

      if (isTypedCustomModel) {
        return this._modelName || null;
      }

      return null;
    };

    const modelConfigDisplayName = getModelConfigDisplayName();

    const convertedData = convertToBlueprintModel({
      metadata: {
        ...this._metadata,
        model: {
          provider: this._modelProvider?.name,
          name: modelName || "",
          model_config_display_name: modelConfigDisplayName,
          model_config_id: isCustomModel ? selectedModel?.id : null,
          base_model: isCustomModel ? selectedModel?.base_model : null,
          parameters,
          api_type: this.selectedAPI || null,
        },
      },
      inference_client_name: this._inferenceClientName,
      provider_base_url_name: this._providerBaseURL,
      provider_id: !isCustomProvider ? undefined : this._providerId || undefined,
    });

    // Decision APIs take questions instead of sampling parameters.
    if (this.selectedAPI === DECISIONS_API && convertedData?.metadata?.model) {
      convertedData.metadata.model.parameters = { questions: this._decisionQuestions };
    }

    if (convertedData?.metadata?.model?.parameters?.response_format?.json_schema) {
      this._JSONSchema = convertedData.metadata?.model?.parameters?.response_format?.json_schema;
    }

    return convertedData;
  }

  @action
  createCustomParameter(key: string) {
    const newParam = new ParameterStore({
      param_id: key,
      name: key,
      component_type: undefined,
      default: null,
      is_disabled: false,
      is_custom: true,
      value: 0,
    });

    this._parameters.set(key, newParam);

    this.updateExternalData();
  }

  @action
  updateMetadata(metadata: PromptBlueprint["metadata"]) {
    this._metadata = metadata;
  }

  @action
  deleteCustomParameter(key: string) {
    this._parameters.delete(key);

    this.updateExternalData();
  }

  @action
  updateModel(modelUpdate: Partial<Model>) {
    this._modelName = modelUpdate.name;
    this._modelProvider = this._providerList?.find(
      p => p.name.toLowerCase() === modelUpdate.provider?.toLowerCase(),
    );
    this.setParametersFromArray(modelUpdate.parameters || []);
    this.updateExternalData();
  }

  @action
  updateModelParameters(parameters: Parameter[]) {
    this._parameters.clear();

    parameters.forEach(param => {
      this._parameters.set(param.param_id, new ParameterStore(param));
    });
  }

  private snapshotDisabledParamIds(): Set<string> {
    const ids = new Set<string>();
    this._parameters.forEach((store, id) => {
      if (store.isDisabled) {
        ids.add(id);
      }
    });
    return ids;
  }

  @action
  updateModelName(modelName: string) {
    const selectedModelName =
      this._modelProvider?.name === HUGGINGFACE_PROVIDER
        ? this._inferenceClientName
        : this._modelName;
    if (modelName === selectedModelName) {
      return;
    }

    const previousPromptData = this.promptData;
    const previousDisabledIds = this.snapshotDisabledParamIds();

    if (this._modelProvider?.name === HUGGINGFACE_PROVIDER) {
      this._modelName = undefined;
      this._inferenceClientName = modelName;
    } else {
      this._modelName = modelName;
    }

    const selectedModel = this._models.find(
      m => m.llm_model_name === modelName || m.display_name === modelName,
    );

    if (selectedModel) {
      const newModelParams = this.getParametersForModel(this._modelProvider?.name, modelName);
      const mergedParams = mergeModelParametersOnSwitch(
        previousPromptData,
        newModelParams,
        previousDisabledIds,
      );
      this.setParametersFromArray(mergedParams);
      this._isPromptChat = selectedModel.is_chat;

      const providerName = this._modelProvider?.name;
      const isOpenAIManaged =
        providerName === "openai" ||
        providerName === "openai.azure" ||
        (this._modelProvider?.workspace_id !== undefined &&
          this._modelProvider?.workspace_id !== null &&
          this._modelProvider?.client === "openai");
      if (!isOpenAIManaged) {
        const supportedApis = selectedModel.supported_apis ?? [];
        if (supportedApis.length > 0) {
          if (!this._selectedAPI || !supportedApis.includes(this._selectedAPI)) {
            this._selectedAPI = supportedApis[0];
          }
        } else {
          this._selectedAPI = null;
        }
      }
    }

    this.updateExternalData();
  }

  @action
  clearModelData() {
    this._modelName = undefined;
    this._modelProvider = undefined;
    this._parameters.clear();

    this.updateExternalData();
  }

  @action
  updateModelFromPromptData(promptData: Partial<PromptBlueprint>) {
    this._modelName =
      promptData?.metadata?.model?.model_config_display_name || promptData?.metadata?.model?.name;
    this._modelProvider = this._providerList?.find(
      p => p.name.toLowerCase() === promptData?.metadata?.model?.provider?.toLowerCase(),
    );

    this._inferenceClientName = promptData.inference_client_name;
    if (promptData?.metadata?.model?.parameters?.questions) {
      this._decisionQuestions = promptData.metadata.model.parameters.questions;
    }
    const modelNameForParams =
      promptData?.metadata?.model?.provider === HUGGINGFACE_PROVIDER
        ? promptData.inference_client_name
        : promptData?.metadata?.model?.name;
    const isKnownModel = this._models.some(
      m => m.llm_model_name === modelNameForParams || m.display_name === modelNameForParams,
    );

    if (isKnownModel && modelNameForParams) {
      this.setParametersFromArray(
        this.getParametersForModel(promptData?.metadata?.model?.provider, modelNameForParams),
      );
    }
  }

  @action
  updateProvider(provider: string) {
    if (provider === this._modelProvider?.name) {
      return;
    }

    this._modelProvider = this._providerList?.find(
      p => p.name.toLowerCase() === provider.toLowerCase(),
    );

    this._models = [];
    this._providerId = null;

    if (provider === HUGGINGFACE_PROVIDER) {
      this._modelName = undefined;
      this._providerBaseURL = undefined;
    } else {
      this._inferenceClientName = undefined;
      this._modelName = undefined;

      const selectedProvider = this._providerList?.find(p => p.name === provider);

      if (selectedProvider?.base_url) {
        this._providerId = selectedProvider.id;
        this._providerBaseURL = null;
        this._modelProvider = selectedProvider;
      }
    }

    // Set default API version for OpenAI providers, clear for non-OpenAI
    const isOpenAI = provider === "openai" || provider === "openai.azure";
    const selectedCustomProvider = this._providerList?.find(p => p.name === provider);
    const isCustomOpenAI =
      selectedCustomProvider?.workspace_id !== undefined &&
      selectedCustomProvider?.workspace_id !== null &&
      selectedCustomProvider?.client === "openai";

    if (isOpenAI || isCustomOpenAI) {
      if (!this._selectedAPI) {
        this._selectedAPI = "chat-completions";
      }
    } else if (provider === "openrouter") {
      this._selectedAPI = "chat";
    } else {
      this._selectedAPI = null;
    }

    this.updateExternalData();
  }

  @action setProviderUponBaseURL(provider: string) {
    const selectedProvider = this._providerList?.find(
      p => p.name === this._providerBaseURL || p.id === this._providerId,
    );

    if (selectedProvider) {
      this._modelProvider = selectedProvider;
      this._providerId = selectedProvider.workspace_id ? selectedProvider.id : null;
      this._providerBaseURL = null;
    } else {
      this.updateProvider(this._modelProvider?.name || provider);

      return;
    }

    this.updateExternalData();
  }

  @action
  setModels(models: ApiModel[]) {
    this._models = models;

    if (this._modelProvider) {
      const defaultModelName = this.findDefaultModelForProvider(this._modelProvider?.name);

      if (defaultModelName && !this._modelName && !this._inferenceClientName) {
        const currentModelExists =
          this._modelName &&
          models.some(
            m => m.llm_model_name === this._modelName || m.display_name === this._modelName,
          );

        if (!currentModelExists && !this._modelName) {
          const defaultModel = models.find(
            m => m.llm_model_name === defaultModelName || m.display_name === defaultModelName,
          );
          if (
            this._selectedAPI &&
            defaultModel?.supported_apis?.length &&
            !defaultModel.supported_apis.includes(this._selectedAPI)
          ) {
            return;
          }

          this._modelName = defaultModelName;
          this.setParametersFromArray(
            this.getParametersForModel(this._modelProvider?.name, defaultModelName),
          );
          if (defaultModel) {
            this._isPromptChat = defaultModel.is_chat;
          }

          this.updateExternalData();
        }
      }
    }
  }

  @action
  setIsPromptChat(isPromptChat: boolean) {
    this._isPromptChat = isPromptChat;
  }

  @action
  updateExternalData() {
    if (this.onUpdate && this.promptData) {
      this.onUpdate(this.promptData);
    }
  }

  @action
  setJSONSchema(jsonSchema: JSONSchema | null) {
    this._JSONSchema = jsonSchema;

    const responseFormatParam = this._parameters.get("response_format");

    if (responseFormatParam && this._JSONSchema) {
      responseFormatParam.updateValue({
        type: "json_schema",
        json_schema: this._JSONSchema,
      });
    } else if (responseFormatParam && !this._JSONSchema) {
      responseFormatParam.updateValue(this._JSONSchema);
    }
  }

  @computed get JSONSchema() {
    return this._JSONSchema;
  }

  @computed get decisionQuestions() {
    return this._decisionQuestions;
  }

  @action
  setDecisionQuestions(questions: DecisionQuestion[]) {
    this._decisionQuestions = questions;
    this.updateExternalData();
  }

  @computed get messages() {
    return this._messages;
  }

  @action
  setMessages(messages?: string[]) {
    this._messages = messages;
  }

  @computed get providerList() {
    return this._providerList;
  }

  @action
  setProviderList(providerList: Provider[], promptData: Partial<PromptBlueprint>) {
    this._providerList = providerList;

    if (!this._modelProvider && !promptData.provider_base_url_name) {
      this._modelProvider = providerList?.find(
        p => p.id === promptData?.provider_id || p.name === promptData?.metadata?.model?.provider,
      );
    }

    if (this._modelProvider?.name === HUGGINGFACE_PROVIDER && !this._inferenceClientName) {
      this._inferenceClientName =
        promptData.inference_client_name ||
        promptData.metadata?.model?.model_config_display_name ||
        undefined;
    }
  }

  @computed get selectedProviderId() {
    return this._modelProvider?.id;
  }

  @computed get selectedAPI(): string | null {
    if (!this._selectedAPI) {
      const providerName = this._modelProvider?.name;
      const isOpenAI = providerName === "openai" || providerName === "openai.azure";
      const isCustomOpenAI =
        this._modelProvider?.workspace_id !== undefined &&
        this._modelProvider?.workspace_id !== null &&
        this._modelProvider?.client === "openai";
      if (isOpenAI || isCustomOpenAI) {
        return "chat-completions";
      }
      if (providerName === "openrouter") {
        const selectedModel = this._models.find(
          m => m.llm_model_name === this._modelName || m.display_name === this._modelName,
        );
        const apis = selectedModel?.supported_apis ?? [];
        if (apis.includes("video")) {
          return "video";
        }
        if (apis.includes("speech")) {
          return "speech";
        }
        if (apis.includes("images")) {
          return "images";
        }
        if (apis.includes(DECISIONS_API) && !apis.includes("chat")) {
          return DECISIONS_API;
        }
        return "chat";
      }
    }
    return this._selectedAPI ?? null;
  }

  @action
  updateSelectedAPI(api: string | null) {
    if (api === this._selectedAPI) {
      return;
    }

    const previousPromptData = this.promptData;
    const previousDisabledIds = this.snapshotDisabledParamIds();
    this._selectedAPI = api;

    if (api && this._modelProvider) {
      // If the currently selected model already supports the new API (e.g. a
      // multimodal chat+image model when toggling Chat<->Images), keep it put
      // instead of resetting to a default — that reset was making the selection
      // "disappear" when switching APIs back and forth.
      const currentModel = this._models.find(
        m => m.llm_model_name === this._modelName || m.display_name === this._modelName,
      );
      if (currentModel && this.modelMatchesSelectedApi(currentModel)) {
        this.updateExternalData();
        return;
      }

      const defaultModel =
        this.resolveDefaultModelRow() ?? this._models.find(m => this.modelMatchesSelectedApi(m));

      if (defaultModel) {
        this._modelName = defaultModel.llm_model_name;
        const newModelParams = this.getParametersForModel(
          this._modelProvider.name,
          defaultModel.llm_model_name,
        );
        const mergedParams = mergeModelParametersOnSwitch(
          previousPromptData,
          newModelParams,
          previousDisabledIds,
        );
        this.setParametersFromArray(mergedParams);
        this._isPromptChat = defaultModel.is_chat;
      } else {
        this._modelName = undefined;
        this.setParametersFromArray([]);
      }
    }

    this.updateExternalData();
  }

  @action
  setOnUpdateCallback(callback?: (promptData: Partial<PromptBlueprint>) => void) {
    this.onUpdate = callback;
  }
}

export const ParametersContext = createContext<ParametersStore | undefined>(undefined);

export const ParameterProvider = observer(
  ({
    children,
    promptData,
    displayAsPopover,
    isPromptChat,
    isPlayground,
    onUpdate,
    onModeChange,
    isOpen,
    messages,
  }: ParameterProviderProps) => {
    const { store: providerStore } = useLLMProviderStore();
    const { store: modelsStore, setIsEnabled } = useModelsStore({
      isEnabledByDefault: !displayAsPopover || isPlayground,
    });

    const { providers } = providerStore;
    const { models, modelsLoading } = modelsStore;

    const store = useLocalObservable(
      () => new ParametersStore(promptData, providers, isPromptChat, onUpdate),
    );
    const playground = useContext(PlaygroundStoreContext);

    // Ensure the onUpdate callback is always current
    useEffect(() => {
      store.setOnUpdateCallback(onUpdate);
    }, [store, onUpdate]);

    useEffect(() => {
      playground?.setStreamingRequiredByModel(store.requiresStreaming);
      playground?.setStreamingDisallowedByModel(store.disallowsStreaming);
    }, [playground, store.requiresStreaming, store.disallowsStreaming]);

    // Set messages when they change
    useEffect(() => {
      store.setMessages(messages);
    }, [store, messages]);

    useEffect(() => {
      // Keep models loaded in the playground so api_type can be derived on load.
      setIsEnabled(isPlayground || !!isOpen);
    }, [isOpen, isPlayground, setIsEnabled]);

    useEffect(() => {
      const isHuggingFace = promptData?.metadata?.model?.provider === HUGGINGFACE_PROVIDER;
      const promptModelName = isHuggingFace
        ? promptData?.inference_client_name
        : promptData?.metadata?.model?.model_config_display_name ||
          promptData?.metadata?.model?.name;
      const storeModelName = isHuggingFace ? store.inferenceClientName : store.modelName;

      if (promptModelName !== storeModelName && storeModelName) {
        store.updateModelFromPromptData(promptData as Partial<PromptBlueprint>);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promptData]);

    useEffect(() => {
      if (promptData?.metadata?.model?.parameters?.response_format?.json_schema) {
        store.setJSONSchema(promptData.metadata.model.parameters.response_format.json_schema);
      }
    }, [promptData?.metadata?.model?.parameters?.response_format, store]);

    useEffect(() => {
      if (providers) {
        store.setProviderList(providers, promptData || {});

        if (promptData?.provider_base_url_name) {
          store.setProviderUponBaseURL(promptData?.metadata?.model?.provider || "");
        }
      }
    }, [providers, store, promptData?.provider_base_url_name, promptData]);

    useEffect(() => {
      if (store.selectedProviderId && store.selectedProviderId !== modelsStore.selectedProviderId) {
        modelsStore.setSelectedProviderId(store.selectedProviderId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.selectedProviderId, modelsStore.selectedProviderId]);

    // Scope model fetch to the selected OpenRouter api_type.
    useEffect(() => {
      const scopedApiType =
        store.provider?.name === "openrouter" ? (store.selectedAPI ?? undefined) : undefined;
      if (scopedApiType !== modelsStore.selectedApiType) {
        modelsStore.setSelectedApiType(scopedApiType);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.provider?.name, store.selectedAPI, modelsStore.selectedApiType]);

    useEffect(() => {
      if (!modelsLoading && store.provider) {
        store.setModels(models);

        const isHuggingFace = store.provider.name === HUGGINGFACE_PROVIDER;
        const modelName = isHuggingFace
          ? promptData?.inference_client_name
          : promptData?.metadata?.model?.name;

        if (modelName && store.provider.name === promptData?.metadata?.model?.provider) {
          const modelParams = isHuggingFace
            ? models.find(
                model => model.display_name === modelName || model.llm_model_name === modelName,
              )?.params || []
            : getModelParams(models, modelName);
          const mappedData = mapInitialDataWithExistingParams(promptData, modelParams);

          if (mappedData?.metadata?.model?.parameters) {
            store.updateModelParameters(mappedData.metadata.model.parameters as Parameter[]);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      modelsLoading,
      models,
      promptData?.metadata?.model?.name,
      promptData?.inference_client_name,
    ]);

    useEffect(() => {
      if (!store.provider || store.isPromptChat === isPromptChat) {
        return;
      }

      store.setIsPromptChat(isPromptChat);
    }, [isPromptChat, store]);

    // Push derived api_type to the parent blueprint when selectedAPI resolves.
    useEffect(() => {
      const dispose = reaction(
        () => store.selectedAPI,
        () => {
          store.updateExternalData();
        },
      );
      return dispose;
    }, [store]);

    useEffect(() => {
      if (!onModeChange) {
        return;
      }
      const dispose = reaction(
        () => store.isPromptChat,
        newIsChat => {
          if (newIsChat !== undefined && newIsChat !== isPromptChat) {
            onModeChange(newIsChat);
          }
        },
      );
      return dispose;
    }, [store, onModeChange, isPromptChat]);

    return <ParametersContext.Provider value={store}>{children}</ParametersContext.Provider>;
  },
);

export const useParameters = (): ParametersStore => {
  const context = useContext(ParametersContext);
  if (context === undefined) {
    throw new Error("useParameters must be used within a ParameterProvider");
  }
  return context;
};
